// BP-1 (2026-10-30): each product of a lender has its own API batch with its own versions. Tab 3 shows one product's
// batch at a time; the choice is the address's `p` (a product slug, or `lender` = the whole-lender batch a lender from
// before still has), so 3.1, 3.2 and 3.3 and the playground stay on the same product.
import { useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { ofCode, productShort } from '@/lib/batch'
import { useCodeStore } from '@/store/codeStore'
import type { Lender } from '@/types'

/** `p` value of the whole-lender batch */
export const WHOLE_LENDER = 'lender'

export type ProductChoice = { value: string; label: string; productSlug: string | null; versions: number }

/** The products to pick from (every product of the lender, plus the whole-lender batch when it has one) and the chosen one. */
export function useBatchProduct(lender: Lender) {
  const [params, setParams] = useSearchParams()
  const batches = useCodeStore((s) => s.batches)
  const choices = useMemo<ProductChoice[]>(() => {
    const list: ProductChoice[] = lender.products.map((p) => ({ value: p.slug, label: p.name || productShort(p.slug), productSlug: p.slug, versions: ofCode(batches, lender.slug, p.slug).length }))
    const whole = ofCode(batches, lender.slug, null).length
    if (whole) list.push({ value: WHOLE_LENDER, label: 'Whole lender (before)', productSlug: null, versions: whole })
    return list
  }, [batches, lender.products, lender.slug])
  const asked = params.get('p')
  const chosen = choices.find((c) => c.value === asked) ?? choices.find((c) => c.versions > 0) ?? choices[0] ?? null
  const choose = (value: string) => {
    const next = new URLSearchParams(params)
    next.set('p', value)
    setParams(next, { replace: true })
  }
  return { choices, chosen, productSlug: chosen ? chosen.productSlug : null, choose }
}

/** The buttons above a batch list: one per product (with its number of versions). */
export function ProductPicker({ choices, chosen, onChoose }: { choices: ProductChoice[]; chosen: ProductChoice | null; onChoose: (value: string) => void }) {
  if (!choices.length) return <p className="mb-3 text-xs text-muted">This lender has no product yet: add one on tab 2 first; each product gets its own API batch.</p>
  return (
    <div className="mb-3 flex flex-wrap items-center gap-1" role="group" aria-label="Product" data-testid="product-picker">
      <span className="mr-1 text-xs text-muted">Product:</span>
      {choices.map((c) => (
        <button
          key={c.value}
          type="button"
          className={`btn btn-sm ${chosen?.value === c.value ? 'btn-primary' : 'btn-secondary'}`}
          aria-pressed={chosen?.value === c.value}
          onClick={() => onChoose(c.value)}
          data-testid={`product-${c.value}`}
        >
          {c.label} <span className="ml-1 opacity-70">{c.versions ? `· ${c.versions}` : '· no batch'}</span>
        </button>
      ))}
    </div>
  )
}
